import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import GetAppIcon from '@material-ui/icons/GetApp';
import HighlightOff from '@material-ui/icons/HighlightOff';
import fileDownload from 'js-file-download';
import { getBinary } from '../utils/apiUtils';
import { ErrorContext, PermissionsContext } from '../utils/constants';
import EnhancedTablePagination from './EnhancedTablePagination';

import createSessionStorageState from '../utils/sessionStorageUtil';
import Paper from '@material-ui/core/Paper';
import { useHistory } from "react-router-dom";

import { formatCurrency } from '../utils/currencyFormater';
import { datetimeFormatter, dateFormatter } from '../utils/dateUtils';
import { tableToCsv } from '../utils/tableToCsvUtil';

export const source = [];

function desc(a, b, orderBy) {

  let stringA = ((typeof a[orderBy]) === "string") ? a[orderBy].toLowerCase() : a[orderBy];
  let stringB = ((typeof b[orderBy]) === "string") ? b[orderBy].toLowerCase() : b[orderBy];

  if (stringB < stringA) {
    return -1;
  }
  if (stringB > stringA) {
    return 1;
  }
  return 0;
}

function dateSorter(a, b, orderBy) {
  let dateA = new Date(a[orderBy]);
  let dateB = new Date(b[orderBy]);
  return dateA.getTime() - dateB.getTime();
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy, headCells) {
  const column = headCells.find(item => item.id === orderBy);
  if (column && (column.format === 'datetime' || column.format === 'date')) {
    return order === 'desc'
      ? (a, b) => dateSorter(a, b, orderBy)
      : (a, b) => -dateSorter(a, b, orderBy);
  }
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {props.headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding="default"
            sortDirection={orderBy === headCell.id ? order : false}
            className={classes.headerCell}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={order}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  allowExport: PropTypes.bool
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  padding16: {
    paddingLeft: '16px'
  },
  padding16Download: {
    paddingLeft: '16px',
    width: '5px'
  },
  cursorPointer: {
    cursor: 'pointer'
  },
  headerCell: {
    fontWeight: 'bold'
  },
  downloadIcon: {
    position: 'relative',
    top: '3px',
  },
  removeAttchIcon: {
    position: 'relative',
    left: '10px',
    fontSize: '16px'
  },
  inputStyle: {
    width: '80%',
    border: '1px solid #ced4da',
    fontSize: '16px',
    transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: '4px',
    backgroundColor: 'white',
    paddingLeft: '10px'
  },
  dragFilesRow: {
    textAlign: 'center',
    fontWeight: '700',
    marginTop: '1rem',
    marginBottom: '0rem',
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
    paddingLeft: '1rem',
    position: 'relative',
    borderTop: '2px solid #e0e0e0',
    borderBottom: '2px solid #e0e0e0',
  },
  attachCell: {
    borderTop: '2px solid #e0e0e0',
  }
}));

export default function EnhancedTable(props) {
  const classes = useStyles();
  const history = useHistory();
  const { triggerError } = useContext(ErrorContext);

  const rowsPerPageKey = `table.rowsPerPage.${props.id || ''}`;
  const useRowsPerPageState = createSessionStorageState(rowsPerPageKey);

  const currentPageKey = `table.currentPage.${props.id || ''}`;
  const useCurrentPageState = createSessionStorageState(currentPageKey);

  // STATE
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('date');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = useCurrentPageState(0);
  const [rowsPerPage, setRowsPerPage] = useRowsPerPageState(props.rowsPerPage || 5);
  const [rowsPerPageOptions] = React.useState(props.rowsPerPageOptions ? props.rowsPerPageOptions : [5, 10, 25]);

  const { isAdmin } = useContext(PermissionsContext);
  const selectableValues = props.labels;
  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  };

  const handleClick = (event, name, row) => {
    if (props.onClick) {
      props.onClick(event, row);
      return;
    }
    else if (props.clickData) {
      let id = row[props.clickData.key];
      history.push(props.clickData.url + id);
      return;
    }

    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportClicked = async _ => {
    const data = await tableToCsv(props.rows, props.headCells);
    const filename = `${props.exportFilename || 'initiatives'}.csv`;
    fileDownload(data, filename);
  };

  const getAttachmentHref = (id, filename) => {
    return window.location.origin + '/downloads/' + id + '/files/' + filename;
  };

  const handleDownloadAttachment = (e, initiativeId, id, filename) => {
    e && e.preventDefault();

    getBinary(`/downloads/${initiativeId}/files/${id}`, 'GET', '', (fileContent) => {
      fileDownload(fileContent, filename);
    }, () => triggerError({
      title: 'Attachment Download Error',
      description: `We were unable to download the attachment. Please try again.`
    }));
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, props.rows.length - page * rowsPerPage);
  const hasFiles = props.headCells.map(h => h.id === 'filename'? true: false);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={hasFiles.includes(true) ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.rows.length}
              headCells={props.headCells}
            />
            <TableBody>
              {stableSort(props.rows, getSorting(order, orderBy, props.headCells))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      onClick={event => handleClick(event, row.name, row)}
                      tabIndex={-1}
                      key={row.id}
                      className={classes.cursorPointer}
                    >
                      {
                        props.headCells.map((cell, index) => {
                          if (index === 0) {
                            if (typeof row.saved_filename === 'undefined') {
                              return (
                                <TableCell component="th" id={labelId} key={labelId} scope="row" className={classes.padding16}>
                                  { row[props.headCells[0].id] }
                                </TableCell>
                              )
                            }
                            else {
                              return (
                                <TableCell component="th" id={labelId} key={labelId} scope="row" className={classes.padding16Download, hasFiles.includes(true) ? classes.attachCell : null}>
                                  { <a href={getAttachmentHref(row.initiative_id, row.filename)} onClick={(e) => handleDownloadAttachment(e, row.initiative_id, row.id, row.filename)}><GetAppIcon className={classes.downloadIcon} /></a> }
                                </TableCell>
                              )
                            }

                          }
                          if (cell.label === "LABEL") {
                            return (
                              <TableCell align="left" key={`enhanced-table-${index}-${cell.id}`} className={classes.attachCell}>
                                <Select
                                  id={`select-${index}-${cell.id}`}
                                  value={row.label === null ? Object.keys(selectableValues)[0] : row.label}
                                  onChange={props.onChange(row.id)}
                                  className={classes.inputStyle}
                                  
                                >
                                  {
                                    Object.keys(selectableValues || {}).map(key => {
                                      const value = selectableValues[key];
                                      return (<MenuItem value={key} key={'label-' + key} >{value}</MenuItem>);
                                    })

                                  }
                                </Select>
                              </TableCell>
                            )
                          } 
                          else {
                            return (
                              <TableCell align="left" key={`enhanced-table-${index}-${cell.id}`} className={hasFiles.includes(true) ? classes.attachCell : null}>
                                {cell.format === 'datetime' ? datetimeFormatter(row[cell.id]) : cell.format === 'date' ? dateFormatter(row[cell.id]) : cell.format === 'currency' ? formatCurrency(row[cell.id]) : row[cell.id]}
                                {cell.id === "filename" && isAdmin ? <a href='#' onClick={props.onDelete(row.id)}><HighlightOff className={classes.removeAttchIcon} /></a> : null}
                              </TableCell>
                            );
                          }
                        })                        
                      }
                      
                    </TableRow>
                  );
                })
              }

              {hasFiles.includes(true) ? (
                <TableRow>
                  <TableCell colSpan={6} className={classes.dragFilesRow}>DRAG FILES HERE</TableCell>
                </TableRow>
              ) : null }

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div> 
          <EnhancedTablePagination
            allowExport={props.allowExport}
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={props.rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onExportClicked={handleExportClicked}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          /> 
      </Paper>
    </div>
  );
}

EnhancedTable.propTypes = {
  id: PropTypes.string.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  rowsPerPage: PropTypes.number,
  onClick: PropTypes.func,
  clickData: PropTypes.any,
  rows: PropTypes.arrayOf(PropTypes.any).isRequired,
  allowExport: PropTypes.bool,
  exportFilename: PropTypes.string,
  headCells: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onDelete: PropTypes.func
};